import { useState } from "react";

import { map, partition } from "lodash/fp";

import { Stack } from "@mui/material";

import { TabContent } from "@app/analytics/account/WorkspaceSettings/components/styles";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { HeadCell } from "@app/common/hooks/useDataTable";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { pluralize } from "@app/common/utils";
import {
  EnterpriseRole,
  EnterpriseUser,
  EnterpriseWorkspaceInvite,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { usePageTitle } from "@app/utils/hooks";

import MembersTable from "./components/MembersTable";
import WorkspaceActionButton from "./components/WorkspaceActionButton";
import WorkspaceHeader from "./components/WorkspaceHeader";
import AddMemberToWorkspaceModal from "./modals/AddMemberToWorkspaceModal";
import RemoveWorkspaceMemberConfirmationModal from "./modals/RemoveWorkspaceMemberConfirmationModal";

const HEAD_CELLS: HeadCell<EnterpriseUser | EnterpriseWorkspaceInvite>[] = [
  {
    id: "name",
    label: "Name",
    colSpan: 2,
    sortable: true,
    sortBy: (memberOrInvite) =>
      memberOrInvite.type === "user"
        ? memberOrInvite.fullName
        : memberOrInvite.email,
  },
  {
    id: "workspaceRole",
    label: "Workspace Role",
    sortable: true,
    sortBy: (member) =>
      member.type === "user" && member.role === "owner" ? 1 : -1,
  },
  {
    id: "teams",
    label: "Team(s)",
    sortable: true,
    sortBy: (member) =>
      member.type === "user" ? member.teams?.[0]?.name : null,
  },
  { id: "actions", label: "", sortable: false },
];

const People = () => {
  usePageTitle("Workspace Settings - People");

  const {
    removeUsersFromWorkspace,
    updateWorkspaceUser,
    workspace,
    workspaceIsInactive,
  } = useWorkspace();

  const { canInviteUsers, canEditWorkspaceSettings } = useFeatureAccess();

  const [addWorkspaceMemberModalOpen, setAddWorkspaceMemberModalOpen] =
    useState(false);

  const [removing, setRemoving] = useState<boolean>(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    id: string;
    type: "user" | "invite";
  } | null>(null);

  const handleMemberRemoveClick = (id: string, type: "user" | "invite") => () =>
    setMemberToRemove({ id, type });

  const handleMemberRemoveConfirm = async () => {
    if (memberToRemove == null) return;

    setRemoving(true);

    await removeUsersFromWorkspace({
      userIds: memberToRemove.type === "user" ? [memberToRemove.id] : [],
      inviteIds: memberToRemove.type === "invite" ? [memberToRemove.id] : [],
    });

    setRemoving(false);
    setMemberToRemove(null);
  };

  const handleWorkspaceRoleChange =
    (userId: string) => (role: EnterpriseRole) =>
      updateWorkspaceUser({ userId, role });

  const handleRemoveMultipleWorkspaceUsers = async (
    rows: { id: string; type: "user" | "invite" }[],
    onSuccess: (failures: string[]) => void
  ) => {
    const [userRows, inviteRows] = partition(
      (row) => row.type === "user",
      rows
    );

    return removeUsersFromWorkspace(
      {
        userIds: map("id", userRows),
        inviteIds: map("id", inviteRows),
      },
      { onSuccess }
    );
  };

  const numWorkspaceMembers = workspace.users.length + workspace.invites.length;

  return (
    <>
      <AddMemberToWorkspaceModal
        onClose={() => setAddWorkspaceMemberModalOpen(false)}
        open={addWorkspaceMemberModalOpen}
        workspaceName={workspace.name}
      />
      <RemoveWorkspaceMemberConfirmationModal
        loading={removing}
        open={memberToRemove != null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleMemberRemoveConfirm}
      />
      <Stack sx={{ height: "100%", width: "100%" }}>
        <WorkspaceHeader
          title={workspace.name}
          type="workspace"
          subtitle={`${numWorkspaceMembers} ${pluralize(
            "person",
            "people",
            numWorkspaceMembers
          )} in this Workspace`}
          actions={
            canInviteUsers
              ? [
                  <UpsellTooltipWrapper
                    key="add-user"
                    show={workspaceIsInactive}
                  >
                    <WorkspaceActionButton
                      disabled={workspaceIsInactive}
                      text="Add People"
                      onClick={() => setAddWorkspaceMemberModalOpen(true)}
                    />
                  </UpsellTooltipWrapper>,
                ]
              : []
          }
        />
        <TabContent>
          <MembersTable
            context="workspace"
            headCells={HEAD_CELLS}
            invites={workspace.invites}
            onMemberRemove={handleMemberRemoveClick}
            onRemoveMultiple={handleRemoveMultipleWorkspaceUsers}
            onRoleChange={handleWorkspaceRoleChange}
            users={workspace.users}
            canEditTeam={canEditWorkspaceSettings}
          />
        </TabContent>
      </Stack>
    </>
  );
};

export default People;
